import React, { useState, useEffect } from "react";

import "../../styles/Register.css";

import { registerUser } from "../../api/userAuthApi";

const GEO_DATABASE = {
  India: {
    states: [
      "Delhi",
      "Maharashtra",
      "Karnataka",
      "Tamil Nadu",
      "Uttar Pradesh",
    ],

    cities: {
      Delhi: ["New Delhi", "Dwarka", "Rohini"],
      Maharashtra: ["Mumbai", "Pune", "Nagpur"],
      Karnataka: ["Bengaluru", "Mysuru", "Hubli"],
      "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai"],
      "Uttar Pradesh": ["Noida", "Lucknow", "Agra"],
    },
  },

  "United States": {
    states: ["California", "New York", "Texas"],

    cities: {
      California: ["Los Angeles", "San Francisco", "San Diego"],
      "New York": ["New York City", "Buffalo", "Rochester"],
      Texas: ["Houston", "Austin", "Dallas"],
    },
  },

  "United Kingdom": {
    states: ["England", "Scotland", "Wales"],

    cities: {
      England: ["London", "Manchester", "Birmingham"],
      Scotland: ["Edinburgh", "Glasgow", "Aberdeen"],
      Wales: ["Cardiff", "Swansea", "Newport"],
    },
  },

  UAE: {
    states: ["Abu Dhabi", "Dubai", "Sharjah"],

    cities: {
      "Abu Dhabi": ["Abu Dhabi City", "Al Ain"],
      Dubai: ["Dubai City", "Jebel Ali"],
      Sharjah: ["Sharjah City", "Khor Fakkan"],
    },
  },
};

export default function Register({
  onClose,
  onLoginClick,
  onRegisterSuccess,
  initialMobile = "",
  googleSignupToken = "",
  initialGoogleProfile = null,
}) {
  const [formData, setFormData] = useState({
    fullName: initialGoogleProfile?.name || "",
    mobile: initialMobile.replace(/\D/g, "").slice(-10),
    email: initialGoogleProfile?.email || "",
    address: "",
    country: "India",
    state: "Delhi",
    city: "New Delhi",
    pinCode: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const availableStates =
      GEO_DATABASE[formData.country]?.states || [];

    const defaultState = availableStates[0] || "";

    const availableCities =
      GEO_DATABASE[formData.country]?.cities?.[defaultState] || [];

    const defaultCity = availableCities[0] || "";

    setFormData((prev) => ({
      ...prev,
      state: defaultState,
      city: defaultCity,
    }));
  }, [formData.country]);

  useEffect(() => {
    const availableCities =
      GEO_DATABASE[formData.country]?.cities?.[formData.state] || [];

    const defaultCity = availableCities[0] || "";

    setFormData((prev) => ({
      ...prev,
      city: defaultCity,
    }));
  }, [formData.country, formData.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let sanitizedValue = value;

    if (name === "fullName") {
      sanitizedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    if (name === "mobile") {
      sanitizedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "pinCode") {
      sanitizedValue = value.replace(/\D/g, "").slice(0, 6);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newValidationErrors = {};

    if (!formData.fullName.trim()) {
      newValidationErrors.fullName = "Full name is required.";
    }

    if (!googleSignupToken && !/^\d{10}$/.test(formData.mobile)) {
      newValidationErrors.mobile =
        "Mobile number must be exactly 10 digits.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      newValidationErrors.email =
        "Please enter a valid email format.";
    }

    if (!formData.address.trim()) {
      newValidationErrors.address =
        "Address is required.";
    }

    if (formData.pinCode.length !== 6) {
      newValidationErrors.pinCode =
        "Pin Code must be exactly 6 digits.";
    }

    if (Object.keys(newValidationErrors).length > 0) {
      setErrors(newValidationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await registerUser({
        name: formData.fullName,
        ...(!googleSignupToken ? { mobile: formData.mobile } : {}),
        email: formData.email,
        address: formData.address,
        pincode: formData.pinCode,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        ...(googleSignupToken ? { googleSignupToken } : {}),
      });

      if (onRegisterSuccess) {
        onRegisterSuccess(
          response.user,
          response.accessToken
        );
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      setErrors({
        mobile:
          error?.message ||
          "Unable to create account. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cd-reg-card-main">

      <div className="cd-reg-left-brand">

        <div className="cd-reg-glow-orb"></div>

        <div className="cd-reg-logo-block">

          <div className="cd-reg-logo-icon">
            ▶
          </div>

          <div className="cd-reg-logo-text">
            <h2>
              Creators <span>Dost</span>
            </h2>

            <p>
              LEARN • CREATE • GROW • TOGETHER
            </p>
          </div>

        </div>

        <h1 className="cd-reg-main-title">
          Start Your Creator
          <br />
          Journey <span>Today!</span>
        </h1>

        <div className="cd-reg-accent-bar"></div>

        <p className="cd-reg-subtext">
          Join thousands of creators who are learning,
          growing and building successful digital careers.
        </p>

        <div className="cd-reg-features-list">

          <div className="cd-reg-feature-node">

            <div className="cd-reg-icon-ring r-blue-ring">
              📖
            </div>

            <div className="cd-reg-feature-text">
              <h4>100+ Premium Courses</h4>
              <p>Learn from industry experts</p>
            </div>

          </div>

          <div className="cd-reg-feature-node">

            <div className="cd-reg-icon-ring r-indigo-ring">
              👥
            </div>

            <div className="cd-reg-feature-text">
              <h4>Community Access</h4>
              <p>Connect with 50,000+ creators</p>
            </div>

          </div>

          <div className="cd-reg-feature-node">

            <div className="cd-reg-icon-ring r-gold-ring">
              🏅
            </div>

            <div className="cd-reg-feature-text">
              <h4>Certificates</h4>
              <p>Earn industry-recognized certificates</p>
            </div>

          </div>

          <div className="cd-reg-feature-node">

            <div className="cd-reg-icon-ring r-cyan-ring">
              🚀
            </div>

            <div className="cd-reg-feature-text">
              <h4>Career Growth</h4>
              <p>Get mentorship and build your brand</p>
            </div>

          </div>

        </div>

      </div>

      <div className="cd-reg-right-form">

        <button
          type="button"
          className="cd-reg-inner-close-btn"
          onClick={onClose}
          aria-label="Close modal"
        >
          <span className="close-icon-vector">
            ✕
          </span>
        </button>

        <div className="cd-reg-inner-frame">

          <div className="cd-reg-header-text">
            {initialGoogleProfile?.image && (
              <img
                src={initialGoogleProfile.image}
                alt="Google profile"
                referrerPolicy="no-referrer"
                style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }}
              />
            )}
            <h2>{googleSignupToken ? "Review Your Details" : "Create Your Account"}</h2>

            <p>
              {googleSignupToken
                ? "Check and complete your details before signing in."
                : "Fill in the details below to get started"}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="cd-reg-form-grid"
          >

            {/* FULL NAME FIELD */}

            <div className="cd-reg-input-group full-width">

              <label>
                Full Name
              </label>

              <div
                className={`cd-reg-input-wrapper ${
                  errors.fullName
                    ? "input-error-border"
                    : ""
                }`}
              >

                <span className="input-icon">
                  👤
                </span>

                <input
                  type="text"
                  required
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                />

              </div>

              {errors.fullName && (
                <span className="cd-error-label-span">
                  {errors.fullName}
                </span>
              )}

            </div>


            {/* EMAIL FIELD */}

            <div className="cd-reg-input-group full-width">

              <label>
                Email Address
              </label>

              <div
                className={`cd-reg-input-wrapper ${
                  errors.email
                    ? "input-error-border"
                    : ""
                }`}
              >

                <span className="input-icon">
                  ✉️
                </span>

                <input
                  type="email"
                  required
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  readOnly={Boolean(googleSignupToken)}
                />

              </div>

              {errors.email && (
                <span className="cd-error-label-span">
                  {errors.email}
                </span>
              )}

            </div>


            {!googleSignupToken && (
              <div className="cd-reg-input-group full-width">

              <label>
                Mobile Number
              </label>

              <div
                className={`cd-reg-input-wrapper ${
                  errors.mobile
                    ? "input-error-border"
                    : ""
                }`}
              >

                <span className="input-icon">
                  📱
                </span>

                <input
                  type="tel"
                  required
                  name="mobile"
                  maxLength="10"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Enter your 10-digit mobile number"
                />

              </div>

              {errors.mobile && (
                <span className="cd-error-label-span">
                  {errors.mobile}
                </span>
              )}

              </div>
            )}


            {/* ADDRESS FIELD */}

            <div className="cd-reg-input-group full-width">

              <label>
                Address
              </label>

              <div
                className={`cd-reg-input-wrapper ${
                  errors.address
                    ? "input-error-border"
                    : ""
                }`}
              >

                <span className="input-icon">
                  🏠
                </span>

                <input
                  type="text"
                  required
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter your address"
                />

              </div>

              {errors.address && (
                <span className="cd-error-label-span">
                  {errors.address}
                </span>
              )}

            </div>


            {/* COUNTRY SELECTOR */}

            <div className="cd-reg-input-group half-width">

              <label>
                Country
              </label>

              <div className="cd-reg-input-wrapper">

                <span className="input-icon">
                  🌐
                </span>

                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                >

                  {Object.keys(GEO_DATABASE).map(
                    (country) => (
                      <option
                        key={country}
                        value={country}
                      >
                        {country}
                      </option>
                    )
                  )}

                </select>

              </div>

            </div>


            {/* STATE SELECTOR */}

            <div className="cd-reg-input-group half-width">

              <label>
                State
              </label>

              <div className="cd-reg-input-wrapper">

                <span className="input-icon">
                  🗺️
                </span>

                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                >

                  {(
                    GEO_DATABASE[
                      formData.country
                    ]?.states || []
                  ).map((state) => (

                    <option
                      key={state}
                      value={state}
                    >
                      {state}
                    </option>

                  ))}

                </select>

              </div>

            </div>


            {/* CITY SELECTOR */}

            <div className="cd-reg-input-group half-width">

              <label>
                City
              </label>

              <div className="cd-reg-input-wrapper">

                <span className="input-icon">
                  🏙️
                </span>

                <select
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                >

                  {(
                    GEO_DATABASE[
                      formData.country
                    ]?.cities?.[formData.state] || []
                  ).map((city) => (

                    <option
                      key={city}
                      value={city}
                    >
                      {city}
                    </option>

                  ))}

                </select>

              </div>

            </div>


            {/* PIN CODE FIELD */}

            <div className="cd-reg-input-group half-width">

              <label>
                Pin Code
              </label>

              <div
                className={`cd-reg-input-wrapper ${
                  errors.pinCode
                    ? "input-error-border"
                    : ""
                }`}
              >

                <span className="input-icon">
                  📍
                </span>

                <input
                  type="text"
                  required
                  name="pinCode"
                  maxLength="6"
                  value={formData.pinCode}
                  onChange={handleInputChange}
                  placeholder="6-digit pin code"
                />

              </div>

              {errors.pinCode && (
                <span className="cd-error-label-span">
                  {errors.pinCode}
                </span>
              )}

            </div>


            {/* SUBMIT */}

            <button
              type="submit"
              className="cd-reg-submit-btn"
              disabled={isSubmitting}
            >

              {isSubmitting
                ? "Creating Account..."
                : googleSignupToken
                  ? "Create Account & Sign In"
                  : "Create Account"}

              <span className="btn-arrow">
                ➔
              </span>

            </button>

          </form>


          <div className="cd-reg-redirect-footer">

            Already have an account?

            {" "}

            <span
              className="login-link-toggle"
              onClick={onLoginClick}
            >
              Login
            </span>

          </div>


          <div className="cd-reg-separator">

            <div className="sep-line"></div>

            <span>or</span>

            <div className="sep-line"></div>

          </div>


          <div className="cd-reg-security-badge">
            🛡️ Your data is safe and secure with us.
          </div>

        </div>

      </div>

    </div>
  );
}